import React, { useState, useEffect, useCallback } from 'react';
import { Bot, RefreshCw, AlertCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { GlassCard, GlassBadge, GlassButton } from '../../ui';
import { getAIReferrers, AIReferrerSummaryResponse } from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import './AIReferrerWidget.css';

interface AIReferrerWidgetProps {
    projectId: string;
}

const AIReferrerWidget: React.FC<AIReferrerWidgetProps> = ({ projectId }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<AIReferrerSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getAIReferrers(projectId, { days: 30, limit: 5 });
            setData(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load AI referrer data');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Return null when no AI traffic — keeps the dashboard clean for new projects
    if (!loading && !error && data && data.summary.total_ai_visits === 0) {
        return null;
    }

    if (loading) {
        return (
            <GlassCard padding="md" className="air-widget">
                <div className="air-widget-header">
                    <Bot size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="air-widget-title">Pages AI Recommends</h4>
                </div>
                <div className="air-widget-loading">
                    <RefreshCw size={18} className="air-widget-spinner" style={{ color: 'var(--primary-500)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading…</span>
                </div>
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard padding="md" className="air-widget">
                <div className="air-widget-header">
                    <Bot size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="air-widget-title">Pages AI Recommends</h4>
                </div>
                <div className="air-widget-error">
                    <AlertCircle size={16} style={{ color: 'var(--danger)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{error}</span>
                </div>
            </GlassCard>
        );
    }

    if (!data) return null;

    const topPages = data.pages.slice(0, 5);
    const { total_ai_visits, platforms_seen } = data.summary;

    return (
        <GlassCard padding="md" className="air-widget">
            <div className="air-widget-header">
                <Bot size={18} style={{ color: 'var(--primary-500)' }} />
                <h4 className="air-widget-title">Pages AI Recommends</h4>
            </div>

            {/* Summary stats */}
            <div className="air-widget-stat-row">
                <span className="air-widget-stat-label">AI Visits (30d)</span>
                <span className="air-widget-stat-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <TrendingUp size={14} style={{ color: 'var(--success)' }} />
                    {total_ai_visits.toLocaleString()}
                </span>
            </div>
            <div className="air-widget-stat-row">
                <span className="air-widget-stat-label">Platforms</span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {data.platform_totals.slice(0, 3).map(p => (
                        <GlassBadge key={p.platform} variant="info" size="sm">
                            {p.label}
                        </GlassBadge>
                    ))}
                    {platforms_seen > 3 && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                            +{platforms_seen - 3}
                        </span>
                    )}
                </div>
            </div>

            {/* Top pages */}
            {topPages.length > 0 && (
                <div className="air-widget-winners" style={{ marginTop: 8 }}>
                    <h5 style={{
                        margin: '0 0 6px',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                        Top Pages
                    </h5>
                    {topPages.map((p, idx) => {
                        let path = p.page;
                        try { path = new URL(p.page).pathname; } catch { /* keep full */ }
                        return (
                            <div key={idx} className="air-widget-winner-row">
                                <Bot size={12} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                                <span className="air-widget-winner-name" title={p.page}>{path}</span>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                    {p.total_visits.toLocaleString()} visits
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="air-widget-footer">
                <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<ArrowRight size={14} />}
                    iconPosition="right"
                    onClick={() => navigate(`/seo?project=${projectId}`)}
                >
                    View in SEO
                </GlassButton>
            </div>
        </GlassCard>
    );
};

export default AIReferrerWidget;
